import { ApplicationController } from '@aha-app/mvc';
import { SearchDocument } from 'components/framework/models';
import { type SearchDocumentFilters } from 'components/framework/models/generated/types';
import {
  type SearchableModelType,
  SearchableModels,
  isSearchableType,
} from 'components/framework/search_types';
import RecordPickerModal from '../RecordPickerModal';
import HasGlobalContainer, {
  type IHasGlobalContainer,
} from 'components/framework/controllers/concerns/HasGlobalContainer';
import { debounce } from 'lodash';
import {
  type PickableRecord,
  type RecentView,
  recentViewToPickableRecord,
} from '../types/PickableRecord';

export interface RecordPickerOptions {
  actionLabel?: string;
  title?: string;
  searchableTypes?: SearchableModelType[];
  multipleRecords?: boolean;
  createRecord?: boolean;
  showReplaceWithReference?: boolean;
  defaultName?: string;
  filteredRecordType?: SearchableModelType | undefined;
  hidePersonalRecords?: boolean;
  parentRecordType?: string;
  parentRecordId?: RecordId;

  onCancel?: () => void;
  onSelect?: (records: Pick<PickableRecord, 'searchable'>[]) => void;
}

interface RecordPickerModalState extends RecordPickerOptions {
  loading: boolean;
  open: boolean;
  results: PickableRecord[];
  recentRecords: PickableRecord[];
  search: string;
  selection: PickableRecord[];
  replaceWithReference: boolean;
  defaultName?: string;
  hidePersonalRecords: boolean;
  parentRecordType?: string;
  parentRecordId?: RecordId;
}

// This controller has a shared global instance, and no props should be added.
// Instead, pass in the configuration when opening the modal.
interface RecordPickerModalProps {}

const commonFields = ['id', 'name', 'referenceNum', 'color', 'path'];
const commonJoins = {
  workflowStatus: ['name', 'color'],
};

function constructSearchableScope(searchableType: SearchableModelType) {
  const model = SearchableModels[searchableType];

  let scope = model.defaultQuery();

  commonFields.forEach(field => {
    if (model.hasField(field)) {
      scope = scope.select(field);
    }
  });

  Object.keys(commonJoins).forEach(join => {
    if (model.hasField(join)) {
      scope = scope.merge({ [join]: commonJoins[join] });
    }
  });

  return scope;
}

// ideas models are prefixed, but searchable types are not
const normaliseTrackableType = (val: string) => {
  if (val.includes('Ideas::')) {
    val = val.replace(/^Ideas::/, '');
  }
  if (val === 'StrategicImperative') {
    val = 'Goal';
  }
  return val;
};

interface RecordPickerController extends IHasGlobalContainer {}

@HasGlobalContainer(RecordPickerModal)
class RecordPickerController extends ApplicationController<
  RecordPickerModalState,
  RecordPickerModalProps
> {
  static initialState: RecordPickerModalState = {
    loading: true,
    open: false,
    results: [],
    recentRecords: [],
    search: '',
    selection: [],
    title: 'Select Aha! records',
    multipleRecords: true,
    createRecord: false,
    showReplaceWithReference: false,
    replaceWithReference: false,
    defaultName: '',
    filteredRecordType: undefined,
    hidePersonalRecords: false,

    // Default modal handlers
    onCancel: () => {},
    onSelect: () => {},
  };

  async initialize(props) {
    this.resetState();
  }

  resetState() {
    Object.assign(this.state, {
      open: false,
      loading: false,
      results: [],
      recentRecords: [],
      search: '',
      searchableTypes: null,
      selection: [],
      replaceWithReference: false,
      hidePersonalRecords: false,
      parentRecordType: null,
      parentRecordId: null,
    });
  }

  showRecents(): boolean {
    return !this.state.search && this.state.recentRecords.length > 0;
  }

  getVisibleResults() {
    let results = this.showRecents()
      ? this.state.recentRecords
      : this.state.results;

    if (this.state.hidePersonalRecords) {
      results = results.filter(record => record.project?.name);
    }

    return results.filter(record => !this.resultIsSelected(record));
  }

  resultIsSelected(record: PickableRecord): boolean {
    const searchRef = record.searchableIdentifier;
    return this.state.selection.some(
      item => item.searchableIdentifier === searchRef
    );
  }

  // Fetch recently viewed records from the API
  async fetchRecentRecords() {
    try {
      this.state.loading = true;
      const response = await fetch('/recent_views');
      if (!response.ok) {
        throw new Error('Failed to fetch recent records');
      }

      let recentViews = (await response.json()) as RecentView[];

      const { searchableTypes } = this.state;
      if (searchableTypes) {
        recentViews = recentViews.filter(recentView => {
          const type = normaliseTrackableType(recentView.view_trackable_type);
          return isSearchableType(type) && searchableTypes.includes(type);
        });
      }

      this.state.recentRecords = recentViews.map(recentViewToPickableRecord);
    } catch (error) {
      console.error('Error fetching recent records:', error);
      this.state.recentRecords = [];
    } finally {
      this.state.loading = false;
    }
  }

  actionOpenSearchModal(options: RecordPickerOptions) {
    this.resetState();

    // Validate searchableTypes
    if (Array.isArray(options.searchableTypes)) {
      options.searchableTypes =
        options.searchableTypes.filter(isSearchableType);

      // Reset if they're all invalid
      if (options.searchableTypes.length === 0) {
        delete options.searchableTypes;
      }
    } else if ('searchableTypes' in options) {
      delete options.searchableTypes;
    }

    Object.assign(this.state, { ...options, open: true });

    this.renderGlobalContainer();
    void this.fetchRecentRecords();
  }

  async actionPerformSearch(text: string) {
    const trimmed = text.trim();

    this.state.search = trimmed;

    // Ignore whitespace-only searches
    if (!trimmed) {
      this.state.results = [];
      this.state.loading = false;
      return;
    }

    this.state.loading = true;

    return this.debouncedSearch();
  }

  debouncedSearch = debounce(async () => {
    const text = this.state.search;
    const filteredRecordType = this.state.filteredRecordType;

    const filters: SearchDocumentFilters = { query: `${text}*` };
    if (filteredRecordType) {
      filters.searchableType = [filteredRecordType];
    } else if (this.state.searchableTypes) {
      filters.searchableType = this.state.searchableTypes;
    }

    const searchableTypes: SearchableModelType[] =
      this.state.searchableTypes ||
      (Object.keys(SearchableModels) as SearchableModelType[]);

    const searchableScope = searchableTypes
      .map(searchableType => constructSearchableScope(searchableType))
      .reduce((acc, scope) => acc.union(scope));

    const results = await SearchDocument.where(filters)
      .select('name', 'searchableType', 'searchableId')
      .merge({
        searchable: searchableScope,
        project: ['name'],
      })
      .all();

    // Only update results if it's the latest/correct search results
    if (text === this.state.search) {
      this.state.results = results;
      this.state.loading = false;
    }
  }, 250);

  actionAddRecordToSelection(record: PickableRecord) {
    if (!this.resultIsSelected(record)) {
      if (!this.state.multipleRecords) {
        this.state.selection = [];
      }

      this.state.selection.push(record);
    }
  }

  actionRemoveRecordFromSelection(record: PickableRecord) {
    const searchRef = record.searchableIdentifier;
    const index = this.state.selection.findIndex(
      item => item.searchableIdentifier === searchRef
    );
    if (index !== -1) {
      this.state.selection.splice(index, 1);
    }
  }

  actionSelectRecords() {
    if (this.state.onSelect) {
      this.state.onSelect(this.state.selection);
    }

    this.resetState();
  }

  actionClose() {
    this.state.open = false;
  }
}

export default RecordPickerController;
